import React, { useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { addNote } from '../../services/api/visitMetadataApi';

/**
 * AddNotes — Simple text field for adding clinical notes to a visit.
 *
 * Allowed: DOCTOR, NURSE (permission checked by backend).
 */
export default function AddNotes({ visitId, onSuccess, onClose }) {
  const { isDark } = useTheme();
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await addNote(visitId, content.trim());
      setContent('');
      onSuccess?.('Note added successfully');
    } catch (err) {
      setError(err.message || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1rem' }}>📝</span>
          <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
            Add Note
          </h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: isDark ? '#6b7280' : '#9ca3af' }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Text area */}
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type your clinical note here... (Ctrl+Enter to save)"
        rows={4}
        maxLength={5000}
        style={{
          width: '100%',
          padding: '0.75rem',
          borderRadius: '0.625rem',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
          color: isDark ? '#f5f5f7' : '#111827',
          fontSize: '0.8125rem',
          lineHeight: '1.5',
          resize: 'vertical',
          outline: 'none',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
        }}
      />

      {/* Char count */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
        <span style={{ fontSize: '0.625rem', color: isDark ? '#6b7280' : '#9ca3af' }}>
          {content.length}/5000
        </span>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.5rem',
          background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
          color: isDark ? '#fca5a5' : '#dc2626',
          fontSize: '0.75rem',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              background: 'transparent',
              color: isDark ? '#9ca3af' : '#6b7280',
              fontSize: '0.75rem',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={saving || !content.trim()}
          style={{
            padding: '0.5rem 1.25rem',
            borderRadius: '0.5rem',
            border: 'none',
            background: saving || !content.trim()
              ? isDark ? 'rgba(59,130,246,0.3)' : 'rgba(37,99,235,0.3)'
              : 'linear-gradient(135deg, #2563eb, #3b82f6)',
            color: '#fff',
            fontSize: '0.75rem',
            fontWeight: 600,
            cursor: saving || !content.trim() ? 'not-allowed' : 'pointer',
            opacity: saving || !content.trim() ? 0.5 : 1,
          }}
        >
          {saving ? 'Saving...' : '💾 Save Note'}
        </button>
      </div>
    </div>
  );
}
